import React, { useState, useEffect } from 'react';
import { 
  Truck, MapPin, Calendar, Users, Search, Filter, Eye, Edit,
  Trash2, CheckCircle, XCircle, Link2, Clock, Building2,
  Phone, Mail, PawPrint, ArrowRight, RefreshCw, Download
} from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ComboTransportAdmin = ({ password, onBack }) => {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [matchMode, setMatchMode] = useState(false);
  const [matchSelection, setMatchSelection] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [requestsRes, statsRes] = await Promise.all([
        axios.get(`${API}/combo-transports/admin`, {
          headers: { 'X-Admin-Password': password }
        }),
        axios.get(`${API}/combo-transports/admin/stats/summary`, {
          headers: { 'X-Admin-Password': password }
        })
      ]);
      setRequests(requestsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateStatus = async (requestId, status) => {
    try {
      await axios.put(`${API}/combo-transports/admin/${requestId}`, 
        { status },
        { headers: { 'X-Admin-Password': password } }
      );
      toast.success('Status aktualisiert');
      loadData();
      if (selectedRequest?.id === requestId) {
        setSelectedRequest({ ...selectedRequest, status });
      }
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const deleteRequest = async (requestId) => {
    if (!window.confirm('Möchten Sie diese Anfrage wirklich löschen?')) return;
    
    try {
      await axios.delete(`${API}/combo-transports/admin/${requestId}`, {
        headers: { 'X-Admin-Password': password }
      });
      toast.success('Anfrage gelöscht');
      setSelectedRequest(null);
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleMatch = async () => {
    if (matchSelection.length !== 2) {
      toast.error('Bitte wählen Sie genau 2 Anfragen aus');
      return;
    }
    
    try {
      await axios.post(`${API}/combo-transports/admin/match`, null, {
        params: { 
          request_id_1: matchSelection[0],
          request_id_2: matchSelection[1]
        },
        headers: { 'X-Admin-Password': password }
      });
      toast.success('Anfragen wurden als Kombi markiert');
      setMatchMode(false);
      setMatchSelection([]);
      loadData();
    } catch (error) {
      toast.error('Fehler beim Verknüpfen');
    }
  };

  const toggleMatchSelection = (id) => {
    if (matchSelection.includes(id)) {
      setMatchSelection(matchSelection.filter(x => x !== id));
    } else if (matchSelection.length < 2) {
      setMatchSelection([...matchSelection, id]);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'matched': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Offen';
      case 'confirmed': return 'Bestätigt';
      case 'matched': return 'Kombiniert';
      case 'completed': return 'Abgeschlossen';
      case 'cancelled': return 'Storniert';
      default: return status;
    }
  };

  const filteredRequests = requests.filter(req => {
    const matchesSearch = 
      req.organizationName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.originCity.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.destinationCity.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || req.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-500 text-white p-6">
        <div className="max-w-7xl mx-auto">
          <button 
            onClick={onBack}
            className="text-green-100 hover:text-white flex items-center gap-1 mb-2 text-sm"
          >
            ← Zurück zum Admin
          </button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Truck className="h-6 w-6" />
            Kombi-Transport Verwaltung
          </h1>
          <p className="text-green-100">Tierschutz-Transportanfragen verwalten und planen</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <Card className="bg-white">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-gray-800">{stats.total}</p>
                <p className="text-sm text-gray-500">Gesamt</p>
              </CardContent>
            </Card>
            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-yellow-700">{stats.byStatus.pending}</p>
                <p className="text-sm text-yellow-600">Offen</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-green-700">{stats.byStatus.confirmed}</p>
                <p className="text-sm text-green-600">Bestätigt</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-blue-700">{stats.byStatus.matched}</p>
                <p className="text-sm text-blue-600">Kombiniert</p>
              </CardContent>
            </Card>
            <Card className="bg-gray-50 border-gray-200">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-gray-700">{stats.byStatus.completed}</p>
                <p className="text-sm text-gray-600">Abgeschlossen</p>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* List */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <CardTitle>Anfragen</CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant={matchMode ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        setMatchMode(!matchMode);
                        setMatchSelection([]);
                      }}
                      className={matchMode ? 'bg-blue-600' : ''}
                    >
                      <Link2 className="h-4 w-4 mr-2" />
                      {matchMode ? 'Modus beenden' : 'Kombinieren'}
                    </Button>
                    <Button variant="outline" size="sm" onClick={loadData}>
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Search & Filter */}
                <div className="flex gap-2 mt-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Suchen (ID, Verein, Stadt...)"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="h-10 px-3 rounded-md border border-gray-300"
                  >
                    <option value="all">Alle Status</option>
                    <option value="pending">Offen</option>
                    <option value="confirmed">Bestätigt</option>
                    <option value="matched">Kombiniert</option>
                    <option value="completed">Abgeschlossen</option>
                    <option value="cancelled">Storniert</option>
                  </select>
                </div>
              </CardHeader>
              
              <CardContent>
                {matchMode && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-blue-800">
                      <strong>Kombinations-Modus:</strong> Wählen Sie 2 Anfragen aus, um sie zu verknüpfen.
                      {matchSelection.length > 0 && (
                        <span className="ml-2">({matchSelection.length}/2 ausgewählt)</span>
                      )}
                    </p>
                    {matchSelection.length === 2 && (
                      <Button 
                        size="sm" 
                        className="mt-2 bg-blue-600"
                        onClick={handleMatch}
                      >
                        <Link2 className="h-4 w-4 mr-2" />
                        Jetzt kombinieren
                      </Button>
                    )}
                  </div>
                )}
                
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {filteredRequests.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">Keine Anfragen gefunden</p>
                  ) : (
                    filteredRequests.map((req) => (
                      <div
                        key={req.id}
                        className={`p-4 border rounded-lg cursor-pointer transition-all ${
                          selectedRequest?.id === req.id 
                            ? 'border-green-500 bg-green-50' 
                            : matchSelection.includes(req.id)
                            ? 'border-blue-500 bg-blue-50'
                            : 'hover:border-gray-300'
                        }`}
                        onClick={() => {
                          if (matchMode) {
                            toggleMatchSelection(req.id);
                          } else {
                            setSelectedRequest(req);
                          }
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-xs text-gray-400">#{req.id}</span>
                              <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(req.status)}`}>
                                {getStatusLabel(req.status)}
                              </span>
                              {req.matchedWith && (
                                <span className="text-xs text-blue-600 flex items-center gap-1">
                                  <Link2 className="h-3 w-3" />
                                  {req.matchedWith}
                                </span>
                              )}
                            </div>
                            <div className="font-medium text-gray-800 mt-1">
                              {req.organizationName}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                              <MapPin className="h-4 w-4" />
                              {req.originCity} <ArrowRight className="h-3 w-3" /> {req.destinationCity}
                            </div>
                            <div className="flex items-center gap-4 text-xs text-gray-500 mt-2">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {formatDate(req.dateFrom)} - {formatDate(req.dateTo)}
                              </span>
                              <span className="flex items-center gap-1">
                                <PawPrint className="h-3 w-3" />
                                {req.animalCount}x {req.animalType}
                              </span>
                            </div>
                          </div>
                          {matchMode && (
                            <input
                              type="checkbox"
                              checked={matchSelection.includes(req.id)}
                              onChange={() => toggleMatchSelection(req.id)}
                              className="w-5 h-5"
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Detail Panel */}
          <div>
            {selectedRequest ? (
              <Card>
                <CardHeader className="bg-gray-50">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Detail</CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedRequest(null)}
                    >
                      ✕
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  {/* ID & Status */}
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm text-gray-500">#{selectedRequest.id}</span>
                    <span className={`px-3 py-1 rounded-full text-sm ${getStatusColor(selectedRequest.status)}`}>
                      {getStatusLabel(selectedRequest.status)}
                    </span>
                  </div>

                  {/* Organization */}
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-gray-800 flex items-center gap-2 mb-2">
                      <Building2 className="h-4 w-4 text-green-600" />
                      Organisation
                    </h4>
                    <p className="font-medium">{selectedRequest.organizationName}</p>
                    <p className="text-sm text-gray-600">{selectedRequest.contactPerson}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      <a 
                        href={`mailto:${selectedRequest.email}`}
                        className="flex items-center gap-1 text-blue-600 hover:underline"
                      >
                        <Mail className="h-4 w-4" />
                        {selectedRequest.email}
                      </a>
                    </div>
                    <div className="flex items-center gap-1 text-sm mt-1">
                      <Phone className="h-4 w-4 text-gray-400" />
                      {selectedRequest.phone}
                    </div>
                  </div>

                  {/* Route */}
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-gray-800 flex items-center gap-2 mb-2">
                      <MapPin className="h-4 w-4 text-green-600" />
                      Route
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                        <span className="font-medium">{selectedRequest.originCity}</span>
                      </div>
                      {selectedRequest.originAddress && (
                        <p className="text-sm text-gray-500 ml-5">{selectedRequest.originAddress}</p>
                      )}
                      <div className="ml-1.5 border-l-2 border-dashed border-gray-300 h-4"></div>
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                        <span className="font-medium">{selectedRequest.destinationCity}</span>
                      </div>
                      {selectedRequest.destinationAddress && (
                        <p className="text-sm text-gray-500 ml-5">{selectedRequest.destinationAddress}</p>
                      )}
                    </div>
                  </div>

                  {/* Date */}
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-gray-800 flex items-center gap-2 mb-2">
                      <Calendar className="h-4 w-4 text-green-600" />
                      Zeitraum
                    </h4>
                    <p>{formatDate(selectedRequest.dateFrom)} - {formatDate(selectedRequest.dateTo)}</p>
                    {selectedRequest.flexibleDates && (
                      <span className="inline-block mt-1 bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded text-xs">
                        Flexible Termine möglich
                      </span>
                    )}
                  </div>

                  {/* Animals */}
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-gray-800 flex items-center gap-2 mb-2">
                      <PawPrint className="h-4 w-4 text-green-600" />
                      Tiere
                    </h4>
                    <p>{selectedRequest.animalCount}x {selectedRequest.animalType}</p>
                    {selectedRequest.notes && (
                      <p className="text-sm text-gray-500 mt-2 bg-gray-50 p-2 rounded">
                        {selectedRequest.notes}
                      </p>
                    )}
                  </div>

                  {/* Matched With */}
                  {selectedRequest.matchedWith && (
                    <div className="border-t pt-4">
                      <h4 className="font-semibold text-blue-800 flex items-center gap-2 mb-2">
                        <Link2 className="h-4 w-4" />
                        Kombiniert mit
                      </h4>
                      <p className="font-mono text-blue-600">#{selectedRequest.matchedWith}</p>
                    </div>
                  )}

                  {/* Admin Notes */}
                  {selectedRequest.adminNotes && (
                    <div className="border-t pt-4">
                      <h4 className="font-semibold text-gray-800 mb-2">Admin-Notizen</h4>
                      <p className="text-sm bg-yellow-50 p-2 rounded">{selectedRequest.adminNotes}</p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="border-t pt-4 space-y-2">
                    <h4 className="font-semibold text-gray-800 mb-2">Status ändern</h4>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant={selectedRequest.status === 'pending' ? 'default' : 'outline'}
                        onClick={() => updateStatus(selectedRequest.id, 'pending')}
                        className={selectedRequest.status === 'pending' ? 'bg-yellow-500' : ''}
                      >
                        Offen
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedRequest.status === 'confirmed' ? 'default' : 'outline'}
                        onClick={() => updateStatus(selectedRequest.id, 'confirmed')}
                        className={selectedRequest.status === 'confirmed' ? 'bg-green-500' : ''}
                      >
                        Bestätigt
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedRequest.status === 'completed' ? 'default' : 'outline'}
                        onClick={() => updateStatus(selectedRequest.id, 'completed')}
                        className={selectedRequest.status === 'completed' ? 'bg-gray-500' : ''}
                      >
                        Abgeschlossen
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedRequest.status === 'cancelled' ? 'default' : 'outline'}
                        onClick={() => updateStatus(selectedRequest.id, 'cancelled')}
                        className={selectedRequest.status === 'cancelled' ? 'bg-red-500' : ''}
                      >
                        Storniert
                      </Button>
                    </div>
                    
                    <div className="pt-2">
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => deleteRequest(selectedRequest.id)}
                        className="w-full"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Anfrage löschen
                      </Button>
                    </div>
                  </div>

                  {/* Meta */}
                  <div className="border-t pt-4 text-xs text-gray-400">
                    Erstellt: {new Date(selectedRequest.createdAt).toLocaleString('de-DE')}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-gray-50">
                <CardContent className="p-8 text-center text-gray-500">
                  <Eye className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Wählen Sie eine Anfrage aus, um Details anzuzeigen</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComboTransportAdmin;
